import { useEffect, useMemo, useState } from "react"
import { debounce } from "es-toolkit/function"
import { fetchQuery } from "relay-runtime"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import {
  useChPointOfCareSpecialtyQuery,
  useChPointOfCareSpecialtyQuery$data,
} from "@/modules/patients/services/ch/__generated__/useChPointOfCareSpecialtyQuery.graphql.ts"
import { ch_PointOfCareSpecialtyQuery } from "@/modules/patients/services/ch/useChPointOfCareSpecialtyQuery.ts"
import { useDePointOfCareSpecialtyQuery } from "@/modules/patients/services/de/__generated__/useDePointOfCareSpecialtyQuery.graphql.ts"
import { de_PointOfCareSpecialtyQuery } from "@/modules/patients/services/de/useDePointOfCareSpecialtyQuery.ts"
import { RelayEnvironment } from "@/relay-environment"
import { Country } from "@/types/__generated__/union-types"

type PointOfCareSpecialtyCH = NonNullable<
  NonNullable<useChPointOfCareSpecialtyQuery$data["chPointOfCareSpecialties"]>["nodes"]
>[number]
type CareProviderSpecialtyCH = NonNullable<PointOfCareSpecialtyCH["careProviderSpecialty"]>
const getSearchText = (searchValue: string) => {
  if (searchValue.length < 3) return ""

  const [specialtySearchText] = searchValue.trim().split(" - ")
  return specialtySearchText
}

const fetchPointOfCareSpecialtyQuery = (
  country: Country,
  input: (useDePointOfCareSpecialtyQuery | useChPointOfCareSpecialtyQuery)["variables"],
  next: (data: (useDePointOfCareSpecialtyQuery | useChPointOfCareSpecialtyQuery)["response"]) => void,
) => {
  const query = country === "DE" ? de_PointOfCareSpecialtyQuery : ch_PointOfCareSpecialtyQuery
  fetchQuery<useDePointOfCareSpecialtyQuery | useChPointOfCareSpecialtyQuery>(RelayEnvironment, query, input).subscribe(
    { next },
  )
}

const usePointOfCareSpecialtyQuery = () => {
  const country = useCountryParam()
  const [searchText, setSearchText] = useState<string | null>(null)
  const [data, setData] = useState<
    (useDePointOfCareSpecialtyQuery | useChPointOfCareSpecialtyQuery)["response"] | null
  >(null)

  useEffect(() => {
    fetchPointOfCareSpecialtyQuery(country, { searchText: searchText ?? "" }, setData)
  }, [country, searchText])

  const debounceSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        const newSearchText = getSearchText(searchValue)
        if (newSearchText !== searchText) {
          setSearchText(newSearchText)
        }
      }, 500),
    [searchText],
  )

  return {
    chPointOfCareSpecialties: data && "chPointOfCareSpecialties" in data ? data.chPointOfCareSpecialties : undefined,
    dePointOfCareSpecialties: data && "dePointOfCareSpecialties" in data ? data.dePointOfCareSpecialties : undefined,
    fetchPointOfCareSpecialtyQuery,
    debounceSearch,
  }
}

export { usePointOfCareSpecialtyQuery, type CareProviderSpecialtyCH }
